import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/* ============================================================
   Onde a página está rolada, ao entrar e ao voltar

   Trocar de rota não mexe na rolagem: sem ninguém cuidando disso, quem
   estava no fim de um feed longo e abria um prato caía no meio da tela
   nova. Entrar numa tela é sempre pelo topo.

   Voltar é o contrário — e é onde o `<ScrollRestoration/>` do React
   Router não serve AQUI. Ele restaura UMA VEZ, num layout effect, e
   nesse instante o feed ainda é `<div className="screen center">` com um
   spinner: uma página da altura da janela, sem nada pra rolar. O
   navegador corta o alvo para 0, e quando os pratos chegam do Firestore
   a rolagem já está no topo pra sempre. O sintoma engana, porque parece
   que alguém MANDOU ir pro topo — ninguém mandou, a volta é que falhou.

   Por isso a restauração aqui INSISTE: ela espera, quadro a quadro, a
   página ficar alta o bastante e só então salta, de uma vez. Do cache
   em IndexedDB do Firestore isso costuma ser um ou dois quadros, rápido
   demais pra ver.

   E ela desiste na hora em que a pessoa encosta na tela. Puxar a página
   debaixo do dedo de alguém é pior que qualquer posição errada.
   ============================================================ */

/* Quanto esperar a página crescer antes de desistir calado.

   Generoso porque não custa: o salto acontece no MESMO quadro em que o
   conteúdo aparece, então um prazo maior não produz salto tardio — ele
   só cobre o feed que demora a vir pela rede em vez do cache. E o
   primeiro gesto da pessoa cancela tudo de qualquer jeito. */
const PACIENCIA = 2500;

/* A posição de cada entrada do histórico, por `location.key`. Mora na
   memória e não no `sessionStorage` de propósito: recarregar a página
   zera o histórico do app junto, e um número guardado a mais só serviria
   pra mandar alguém pra uma posição de uma sessão que não existe mais. */
const GUARDADO = new Map<string, number>();

/**
 * Entrar vai pro topo; voltar volta pra onde estava.
 *
 * Mora no `Shell` e vale pro app inteiro — uma tela que esquecesse de
 * chamar seria a única que se comporta diferente, que é justamente o
 * tipo de defeito que ninguém liga à tela certa.
 */
export function useRolagem(): void {
  const { key } = useLocation();
  const tipo = useNavigationType();

  /* A chave é lida pelo ouvinte de rolagem lá embaixo, e ela é
     atualizada NO RENDER, não num efeito. O navegador dispara `scroll`
     assim que corta a rolagem na página nova — se a chave só virasse
     depois, esse corte seria gravado como se fosse a posição da tela
     ANTERIOR, e apagaria justamente o número que a volta precisa. */
  const chave = useRef(key);
  chave.current = key;

  /* O navegador tem uma restauração própria que dispara no `popstate` e
     mede a mesma página vazia que quebrou a do React Router. Desligada,
     não há duas tentativas competindo pelo mesmo número. */
  useEffect(() => {
    if (!("scrollRestoration" in history)) return;
    const antes = history.scrollRestoration;
    history.scrollRestoration = "manual";
    return () => { history.scrollRestoration = antes; };
  }, []);

  /* Um ouvinte só, montado uma vez. Anotar a cada rolagem — e não na
     saída da tela — é o que faz o número sobreviver: quando a tela é
     desmontada, o navegador já cortou a rolagem pra caber na página
     nova, e ler ali leria o corte. */
  useEffect(() => {
    const anotar = () => { GUARDADO.set(chave.current, window.scrollY); };
    window.addEventListener("scroll", anotar, { passive: true });
    return () => window.removeEventListener("scroll", anotar);
  }, []);

  useLayoutEffect(() => {
    const alvo = tipo === "POP" ? GUARDADO.get(key) ?? 0 : 0;
    if (alvo <= 0) {
      window.scrollTo(0, 0);
      return;
    }

    let vivo = true;
    const desistirEm = performance.now() + PACIENCIA;
    const parar = () => { vivo = false; };

    /* Qualquer gesto da pessoa cancela. Só gesto: `scroll` ficaria de
       fora porque o nosso próprio salto dispara um. */
    const gestos = ["touchstart", "wheel", "keydown", "pointerdown"] as const;
    gestos.forEach((g) => window.addEventListener(g, parar, { passive: true, once: true }));

    const tentar = () => {
      if (!vivo) return;
      const teto = document.documentElement.scrollHeight - window.innerHeight;
      // Só salta quando a página comporta o alvo INTEIRO. Ir chegando aos
      // poucos, a cada quadro, viraria uma rolagem animada — que é
      // exatamente o efeito que ninguém pediu.
      if (teto >= alvo) { window.scrollTo(0, alvo); return; }
      // Desistir é ficar quieto onde está. A tela encolheu de verdade
      // (prato apagado, filtro mudado) ou o dado não veio; em nenhum dos
      // dois um salto para o fim da página ajudaria alguém.
      if (performance.now() >= desistirEm) return;
      requestAnimationFrame(tentar);
    };
    tentar();

    return () => {
      vivo = false;
      gestos.forEach((g) => window.removeEventListener(g, parar));
    };
    // `tipo` de propósito fora: o que define uma navegação é a chave, e
    // reagir à mudança dele sozinho re-rolaria a mesma tela.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}
